import { c as _c } from "react/compiler-runtime";
import type { VirtualElement as IVirtualElement } from '@popperjs/core';
import { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useTheme } from 'styled-components';
const useLazyRef = initialValFunc => {
  const $ = _c(2);
  const ref = useRef(null);
  if (ref.current === null) {
    let t0;
    if ($[0] !== initialValFunc) {
      t0 = initialValFunc();
      $[0] = initialValFunc;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    ref.current = t0;
  }
  return ref;
};
export interface PopperPortalProps {
  visible?: boolean;
  locked?: boolean;
  z_index?: number;
  children: ReactNode;
}
class VirtualElement implements IVirtualElement {
  private rect = {
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    x: 0,
    y: 0,
    toJSON() {
      return this;
    }
  };
  update(x: number, y: number) {
    this.rect.y = y;
    this.rect.top = y;
    this.rect.bottom = y;
    this.rect.x = x;
    this.rect.left = x;
    this.rect.right = x;
  }
  getBoundingClientRect(): DOMRect {
    return this.rect;
  }
}
const POPPER_OPTIONS: Parameters<typeof usePopper>[2] = {
  placement: 'right-start',
  modifiers: [{
    name: 'offset',
    options: {
      offset: [8, 8]
    }
  }]
};
const MouseTooltip = t0 => {
  const $ = _c(24);
  const {
    visible: t1,
    locked: t2,
    z_index,
    children
  } = t0;
  const visible = t1 === undefined ? true : t1;
  const locked = t2 === undefined ? false : t2;
  const [portal_element, setPortalElement] = useState();
  const [popper_element, setPopperElement] = useState();
  const virtual_element_ref = useLazyRef(_temp);
  const theme = useTheme();
  const {
    styles,
    attributes,
    update
  } = usePopper(virtual_element_ref.current, popper_element, POPPER_OPTIONS);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = document.createElement("div");
      document.body.appendChild(el);
      setPortalElement(el);
      return () => el.remove();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  if ($[2] !== locked || $[3] !== update || $[4] !== virtual_element_ref.current || $[5] !== visible) {
    t5 = () => {
      const handler = t6 => {
        const {
          clientX: x,
          clientY: y
        } = t6;
        virtual_element_ref.current.update(x, y);
        if (!visible || locked) {
          return;
        }
        update?.();
      };
      document.addEventListener("mousemove", handler);
      update?.();
      return () => {
        document.removeEventListener("mousemove", handler);
      };
    };
    $[2] = locked;
    $[3] = update;
    $[4] = virtual_element_ref.current;
    $[5] = visible;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== locked || $[8] !== update) {
    t6 = [update, locked];
    $[7] = locked;
    $[8] = update;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== update || $[11] !== visible) {
    t7 = () => {
      if (!visible) {
        return;
      }
      update?.();
    };
    t8 = [visible, update];
    $[10] = update;
    $[11] = visible;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  if (!portal_element || !visible) {
    return null;
  }
  const t9 = z_index || theme.zindex.popover;
  let t10;
  if ($[14] !== styles.popper || $[15] !== t9) {
    t10 = {
      ...styles.popper,
      zIndex: t9
    };
    $[14] = styles.popper;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== attributes.popper || $[18] !== children || $[19] !== t10) {
    t11 = <div id="mouse-tooltip" ref={setPopperElement} {...attributes.popper} style={t10}>{children}</div>;
    $[17] = attributes.popper;
    $[18] = children;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== portal_element || $[22] !== t11) {
    t12 = createPortal(t11, portal_element);
    $[21] = portal_element;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default MouseTooltip;
function _temp() {
  return new VirtualElement();
}